import { Injectable } from "@angular/core";
import { ITableConfigParamsFilter } from "./table.component";
import { map, of } from "rxjs";
import type { Moment } from "moment";
import { FacilitiesService } from "../../../core/services/facilities.service";
import { CustomerService } from "../../../core/services/customer.service";

type FilterObjectSelectedValue = { id: string; value?: string | Moment }[];

@Injectable({
  providedIn: 'any',
})
export class FiltersList {
  constructor(
    private facilitiesService: FacilitiesService,
    private customersService: CustomerService
  ) {

  }

  getFilterById(filterId: string): ITableConfigParamsFilter | null {
    const filter = this.filters.find((currentFilter) => currentFilter.id === filterId);
    return filter ? this.cloneFilter(filter) : null;
  }

  private cloneFilter(filter: ITableConfigParamsFilter): ITableConfigParamsFilter {
    return {
      ...filter,
      options: filter.options.map((option) => ({ ...option })),
      selectedValue: this.cloneSelectedValue(filter.selectedValue),
      startDate: filter.startDate ? new Date(filter.startDate) : undefined,
      endDate: filter.endDate ? new Date(filter.endDate) : undefined
    };
  }

  private cloneSelectedValue(
    selectedValue: ITableConfigParamsFilter['selectedValue']
  ): ITableConfigParamsFilter['selectedValue'] {
    if (this.isStringArraySelectedValue(selectedValue)) {
      return [...selectedValue];
    }

    if (this.isObjectArraySelectedValue(selectedValue)) {
      return selectedValue.map((value) => ({ ...value }));
    }

    if (selectedValue instanceof Date) {
      return new Date(selectedValue);
    }

    return selectedValue;
  }

  private isStringArraySelectedValue(
    selectedValue: ITableConfigParamsFilter['selectedValue']
  ): selectedValue is string[] {
    return Array.isArray(selectedValue) && selectedValue.every((value) => typeof value === 'string');
  }

  private isObjectArraySelectedValue(
    selectedValue: ITableConfigParamsFilter['selectedValue']
  ): selectedValue is FilterObjectSelectedValue {
    return Array.isArray(selectedValue) && selectedValue.every((value) => typeof value === 'object' && value !== null);
  }

  public filters: ITableConfigParamsFilter[] = [
    {
      type: 'multiSelect',
      label: 'STATUS',
      id: 'status',
      allCheckedByDefault: true,
      tableParamName: 'status',
      selectedValue: ['online', 'offline', 'warning', 'disabled'],
      options: [{
        id: 'online',
        name: 'ONLINE',
        translateName: true,
      }, {
        id: 'offline',
        name: 'OFFLINE',
        translateName: true,
      }, {
        id: 'warning',
        name: 'WARNING',
        translateName: true,
      }, {
        id: 'disabled',
        name: 'DISABLED',
        translateName: true,
      }]
    }, {
      type: 'select',
      label: 'ASSOCIATED',
      id: 'facilities',
      tableParamName: 'facilities',
      checkExistance: true,
      options: [{
        id: 'notLinked',
        name: 'NOT_LINKED',
        value: false,
        translateName: true,
      }, {
        id: 'linked',
        name: 'LINKED',
        value: true,
        translateName: true,
      }]
    }, {
      type: 'select',
      label: 'MANAGED_BY',
      id: 'managedBy',
      tableParamName: 'managedBy',
      options: [{
        id: 'user',
        name: 'USER',
        translateName: true
      }, {
        id: 'system',
        name: 'AUTO_MANAGED',
        translateName: true
      }, {
        id: 'admin',
        name: 'ADMIN',
        translateName: true
      }, {
        id: 'not_managed',
        name: 'NOT_MANAGED',
        translateName: true
      }]
    }, {
      label: 'CUSTOMER',
      id: 'customer',
      tableParamName: 'customer',
      dontResetOnClear: true,
      type: 'select',
      loader: () => this.customersService.getCustomers$().pipe(
        map((data) => data.map((customer) => ({ id: customer.id + '', name: customer.businessName })))
      ),
      options: []
    }, {
      label: 'ORGANIZATION',
      id: 'organization',
      tableParamName: 'organization',
      type: 'select',
      options: [],
      loader: (customerId: string) => this.facilitiesService.getOrganization$(+customerId).pipe(
        map((data) => data.map((organization) => ({ id: organization.id, name: organization.name })))
      ),
      dynamicLoadOn: 'customer'
    }, {
      label: 'BUILDING',
      id: 'building',
      tableParamName: 'building',
      type: 'select',
      options: [],
      loader: (orgId?: string) => this.facilitiesService.getBuildings$(orgId, false).pipe(
        map((data) => data.map((building) => ({ id: building.id!, name: building.name! })))
      ),
      dynamicLoadOn: 'organization'
    }, {
      label: 'UNIT',
      id: 'unit',
      tableParamName: 'unit',
      type: 'select',
      options: [],
      loader: (buildingId?: string) => this.facilitiesService.getUnits$(buildingId!).pipe(
        map((data) => data.map((unit) => ({ id: unit.id!, name: unit.name! })))
      ),
      dynamicLoadOn: 'building'
    }, {
      label: 'ROOM',
      id: 'room',
      tableParamName: 'room',
      type: 'select',
      options: [],
      loader: (unitId?: string) => unitId ? this.facilitiesService.getRooms$(unitId).pipe(
        map((data) => data.map((room) => ({ id: room.id!, name: room.name! })))
      ) : of([]),
      dynamicLoadOn: 'unit'
    }, {
      label: 'ROLE',
      id: 'role',
      tableParamName: 'role',
      type: 'select',
      options: [
        {
          id: 'superUser',
          name: "Super User",
        }, {
          id: 'localAdmin',
          name: "Amministratore",
        }, {
          id: 'user',
          name: "Operatore",
        }
      ]
    }, {
      type: 'multiSelect',
      label: 'ALERT_TYPE',
      id: 'severity',
      tableParamName: 'severity',
      allCheckedByDefault: true,
      selectedValue: ['01', '02', '03', '04', '05', '06', '07', '10'],
      options: []
    }, {
      type: 'select',
      label: 'FILTER_BY_TIME',
      id: 'timestamp',
      tableParamName: 'timestamp',
      selectedValue: 'last24h',
      defaultValue: 'last24h',
      reloadTableData: true,
      options: [{
        id: 'last6h',
        name: "Ultime 6 ore"
      }, {
        id: 'last24h',
        name: "Ultime 24 ore"
      }, {
        id: 'lastWeek',
        name: "Ultima settimana"
      }, {
        id: 'lastMonth',
        name: "Ultimo mese"
      }]
    }
  ]

}
